'use strict';

import CONST from 'Util/constants';
import Animation from '../../Animation';
import VZConfirmationModal from '../../ConfirmationModal';
import Modal from 'bootstrap/js/dist/modal';
import { setScrollClasses } from 'Util/domUtils';

/**
 * @class VZHardware
 */
export default class VZHardware extends HTMLInputElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.hardwareWrapper = this.closest('.js-hardware-phones');
        this.hardwareCardWrapper = this.closest('.js-hardware-device-card');
        this.phoneWrapper = this.closest('.js-hardware-phone-wrapper');
        this.phonesScrollableArea = this.closest('.js-phones-scrollable-area');
        this.productCard = this.closest('.js-product-card');
        this.cardOrderIsReversed = this.productCard.getAttribute('data-reversed') === 'true';

        if (!this.phoneWrapper.classList.contains('out-of-stock')) {
            this.onChange = this.onChange.bind(this);
            this.addEventListener('change', this.onChange);

            [...this.hardwareWrapper.querySelectorAll('.js-quantity-increase')].forEach((el) => el.addEventListener('click', this.constructor.onQuantityChange));

            this.hardwareWrapper.querySelector('.js-quantity-selector').addEventListener('input', this.constructor.onQuantityInput);

            this.phoneWrapper.querySelector('.js-hardware-phone-toggle')?.addEventListener('click', this.toggleDevice.bind(this));

            this.phoneWrapper.querySelector('.js-hardware-phone-delete')?.addEventListener('click', this.showConfirmationModal.bind(this));

            this.phoneWrapper.querySelector('.js-hardware-phone-edit')?.addEventListener('click', this.editConfiguration.bind(this));

            // Listen for scroll events on the phones scrollable area to show/hide scroll indicators
            this.phonesScrollableArea.addEventListener('scroll', (e) => {
                const el = e.currentTarget;
                setScrollClasses(el);
            });

            setScrollClasses(this.phonesScrollableArea);

            // Listen for scroll events on the phones scrollable area to show/hide scroll indicators
            this.phonesScrollableArea.addEventListener('scroll', (e) => {
                const el = e.currentTarget;
                setScrollClasses(el);
            });

            setScrollClasses(this.phonesScrollableArea);

            document.dispatchEvent(new CustomEvent('pdp:update'));

            this.constructor.updateTabIndex();

            // Modals
            this.modal = document.getElementById('hardware-specifications-modal');

            if (this.modal) {
                this.modalInstance = new Modal(this.modal, {});

                this.modal.querySelector('.js-hardware-specifications-modal-close')?.addEventListener('click', this.dismissModal.bind(this));

                this.modalTitle = this.modal.querySelector('.modal-title');
                this.modalBody = this.modal.querySelector('.modal-body');

                this.phoneWrapper.querySelector('.js-hardware-specifications')?.addEventListener('click', this.injectModalContent.bind(this));
            }
        }
    }

    disconnectedCallback() {
        this.removeEventListener('change', this.onChange);

        [...this.hardwareWrapper.querySelectorAll('.js-quantity-increase')].forEach((el) => el.removeEventListener('click', this.constructor.onQuantityChange));

        this.hardwareWrapper.querySelector('.js-quantity-selector').removeEventListener('input', this.constructor.onQuantityInput);

        this.phoneWrapper.querySelector('.js-hardware-phone-toggle')?.removeEventListener('click', this.toggleDevice.bind(this));

        this.phoneWrapper.querySelector('.js-hardware-phone-edit')?.removeEventListener('click', this.editConfiguration.bind(this));

        // Modals
        if (this.modal) this.phoneWrapper.querySelector('.js-hardware-specifications')?.removeEventListener('click', this.injectModalContent.bind(this));
    }

    /**
     * Hides hardware specs modal
     */
    dismissModal() {
        this.modalInstance.hide();
    }

    /**
     * Shows hardware specs modal
     */
    showModal() {
        this.modalInstance.show();
    }

    /**
     * Injects the hardware specs modal with the correct device's specs when toggled and displays the modal
     */
    injectModalContent() {
        const modalContent = this.phoneWrapper.querySelector('.js-hardware-specifications-content');
        const modalTitle = modalContent.getAttribute('data-title');
        const modalBody = modalContent.innerHTML;

        this.modalTitle.innerHTML = modalTitle;
        this.modalBody.innerHTML = modalBody;

        this.showModal();
    }

    /**
     * Overwrites the default checkbox toggle functionality when the hardware device is selected. It then triggers a confirmation modal.
     *
     * @param {Event} e - The event object associated with the toggle action.
     */
    toggleDevice(e) {
        // Ignore modal trigger link
        if (e.target.classList.contains('js-hardware-specifications')) return;

        if (this && this.checked) {
            e.preventDefault();

            this.showConfirmationModal();
        }
    }

    /**
     * Displays a confirmation modal and will remove the selected device when the user confirms.
     */
    showConfirmationModal() {
        const confirmationModal = new VZConfirmationModal();

        confirmationModal.toggleConfirmationModal().then((userConfirmed) => {
            if (userConfirmed) {
                this.checked = !this.checked;

                this.dispatchEvent(new Event('change'));
            }
        });
    }

    /**
     * This function collapses all hardware device cards and expands the current card
     * to allow configuration editing.
     * @function editConfiguration
     * @returns {void}
     */
    editConfiguration() {
        Array.from(document.querySelectorAll('.js-hardware-device-card')).forEach((card) => card && card.classList.add('is-collapsed'));
        this.hardwareCardWrapper?.classList.remove('is-collapsed');
    }

    /**
     * Handles the change event and performs necessary actions, such as updating attributes and DOM elements, based on the checked value.
     *
     * @function onChange
     * @param {Event} e - The event object.
     * @returns {void}
     */
    onChange(e) {
        const phoneWrapper = e.target.parentElement;

        if (e.target.checked) {
            this.hardwareWrapper.classList.add('active');
            this.hardwareCardWrapper.classList.add('is-configured');
            phoneWrapper.parentElement.classList.add('is-selected');

            const attributeContainer = phoneWrapper.querySelector('input:not([disabled]) + .js-variation-label').parentElement;
            attributeContainer.querySelector('input').click();

            // Remove full-width class (delay until all CSS animations finish)
            setTimeout(() => {
                phoneWrapper.querySelector('.js-hardware-phone-content-inner').classList.remove('w-lg-100');
            }, 1000);
        } else {
            this.hardwareWrapper.classList.remove('active');
            this.hardwareCardWrapper.classList.remove('is-configured');

            phoneWrapper.parentElement.classList.remove('is-selected');
            phoneWrapper.querySelector('.js-quantity-selector').value = 1;
            phoneWrapper.querySelector('.js-quantity-decrease').click();

            // Add full-width class back
            phoneWrapper.querySelector('.js-hardware-phone-content-inner').classList.add('w-lg-100');
        }

        this.constructor.updateTabIndex();
    }

    /**
     * Handles the click event on the quantity increase button and displays an error message if the maximum quantity is reached.
     *
     * @function onQuantityChange
     * @param {Event} e - The event object.
     * @returns {void}
     */
    static onQuantityChange(e) {
        const quantityWrapper = e.target.closest('.js-quantity-and-actions');
        const quantitySelector = quantityWrapper.querySelector('.js-quantity-selector');
        const notification = quantityWrapper.querySelector('.js-hardware-quantity-error');

        if (quantitySelector.value === quantitySelector.getAttribute('max')) {
            if (notification.classList.contains('d-none')) {
                notification.classList.remove('d-none');
            } else {
                Animation.shakeSubtle(notification);
            }
        }
    }

    /**
     * Handles the input event on the quantity selector and displays an error message if the maximum quantity is reached.
     *
     * @function onQuantityInput
     * @param {Event} e - The event object.
     * @returns {void}
     */
    static onQuantityInput(e) {
        const quantityWrapper = e.target.closest('vz-variation-attribute');

        if (e.target.value > e.target.getAttribute('max') && !this.cardOrderIsReversed) {
            quantityWrapper.querySelector('.js-hardware-quantity-error').classList.remove('d-none');
        }
    }

    /**
     * Updates the tabindex of child elements based on the selection status of the parent wrapper.
     * If the parent wrapper is selected, the tabindex of child elements is set to 0, otherwise -1.
     *
     * @function updateTabIndex
     * @returns {void}
     */
    static updateTabIndex() {
        const elements = document.querySelectorAll('.js-hardware-phone-config');

        elements.forEach((element) => {
            const wrapper = element.closest('.js-hardware-phone-wrapper');
            const childElements = element.querySelectorAll(CONST.selectors.interactiveElements);
            const tabIndexValue = wrapper && wrapper.classList.contains('is-selected') ? '0' : '-1';

            childElements.forEach((child) => {
                child.setAttribute('tabindex', tabIndexValue);
            });
        });
    }
}

if (!window.customElements.get('vz-hardware')) {
    window.customElements.define('vz-hardware', VZHardware, {
        extends: 'input'
    });
}
